export const normalizeLanguageKey = value => String(value || '').trim().toLowerCase().replace(/\s+/g, '_');

export const CHANNELS = ['all', 'telephony', 'widget', 'browser', 'whatsapp'];
export const ruleIdentity = rule => `${rule.channel || 'all'}:${rule.mode}:${['both', 'all', undefined, null].includes(rule.direction) ? 'all' : rule.direction}:${normalizeLanguageKey(rule.language || 'all')}`;

// Missing channel selectors keep existing configurations applicable everywhere.
export const getMessageRules = (section, key, modes = ['voice', 'chat']) => {
  const rules = section?.[`${key}_rules`];
  if (Array.isArray(rules) && rules.length) return rules.map(rule => {
    const { channel, ...rest } = rule;
    return { ...rest, ...(['voice', 'chat'].includes(channel) ? { mode: rule.mode ?? channel } : channel ? { channel } : {}), language: normalizeLanguageKey(rule.language) };
  });
  return Object.entries(section?.[`${key}_language_wise`] || {}).filter(([, message]) => typeof message === 'string' && message.trim()).flatMap(([language, message]) =>
    modes.map(mode => ({ mode, ...(mode === 'voice' ? { direction: 'both' } : {}), language: normalizeLanguageKey(language), message })));
};

export const normalizeMessageSection = (section, keys, modes = ['voice', 'chat']) => {
  const result = { ...section };
  keys.forEach(key => {
    // Displaying legacy fallbacks must not turn them into stored rules.
    if (Array.isArray(result[`${key}_rules`]) && result[`${key}_rules`].length) {
      result[`${key}_rules`] = getMessageRules(result, key, modes);
    }
  });
  return result;
};

export const normalizeAgentMessageRules = agent => {
  const result = { ...agent };
  if (result.first_message_rules == null) {
    const languages = [...new Set([result.language, ...(result.secondary_languages || [])].filter(Boolean))];
    const rules = ['inbound', 'outbound'].flatMap(direction => typeof result[`first_message_${direction}`] !== 'string' || !result[`first_message_${direction}`].trim() ? [] :
      languages.map(language => ({ mode: 'voice', direction, language, message: result[`first_message_${direction}`] })));
    if (rules.length) result.first_message_rules = rules;
  }
  if (Array.isArray(result.first_message_rules) && result.first_message_rules.length) result.first_message_rules = getMessageRules(result, 'first_message', ['voice']);
  delete result.first_message_inbound;
  delete result.first_message_outbound;
  const sections = {
    knowledge_base: ['start_message'], call_transfer: ['start_message'],
    conversation_hangup: ['hangup_message'], conversation_termination: ['termination_message'],
    conversation_turn_timeout: ['turn_timeout_message', 'last_message'], voice_mail: ['voice_mail_leave_message'],
  };
  Object.entries(sections).forEach(([section, keys]) => {
    if (result[section]) result[section] = normalizeMessageSection(result[section], keys,
      ['call_transfer', 'conversation_termination', 'voice_mail'].includes(section) ? ['voice'] : ['voice', 'chat']);
  });
  result.custom_tools = (result.custom_tools || []).map(tool => normalizeMessageSection(tool, ['start_message']));
  return result;
};

export const validateMessageRules = (rules, maxLength = 500, includeLanguage = true) => {
  const seen = new Set();
  for (const rule of rules) {
    if (!['all', 'voice', 'chat'].includes(rule.mode) || (includeLanguage && !rule.language)) return includeLanguage ? 'Select a mode and language for every message.' : 'Select a mode for every suffix.';
    if (!CHANNELS.includes(rule.channel || 'all')) return 'Select a supported channel.';
    if (rule.direction != null && !['inbound', 'outbound', 'both', 'all'].includes(rule.direction)) return 'Select a supported direction.';
    const key = ruleIdentity(rule);
    if (seen.has(key)) return includeLanguage ? 'Use one message per channel, mode, direction and language.' : 'Use one suffix per channel, mode and direction.';
    seen.add(key);
    if (typeof rule.message !== 'string' || rule.message.length > maxLength) return `Messages must contain at most ${maxLength} characters.`;
    if (maxLength !== 1000 && !rule.message.trim()) return 'Enter a message or remove the empty rule.';
  }
  return '';
};

// Keep saved chat messages visible in the shared editor until an explicit edit.
const legacyChatMessageKeys = {
  first_message: 'opening_message',
  hangup_message: 'closing_message',
  start_message: 'transfer_message',
};

export const getAgentMessageRules = (agent, section, key) => {
  const source = section ? agent?.[section] : agent;
  const rules = getMessageRules(source, key);
  if (source?.[`${key}_rules`]?.some(rule => ['all', 'chat'].includes(rule.mode ?? rule.channel))) return rules;
  const legacyKey = legacyChatMessageKeys[key];
  const chatRules = getMessageRules(agent?.chat, legacyKey, ['chat']);
  const fallback = agent?.chat?.[legacyKey];
  if (typeof fallback === 'string' && (fallback.trim() || key === 'first_message')) {
    const languages = [...new Set([agent.language, ...(agent.secondary_languages || [])].filter(Boolean))];
    languages.forEach(language => {
      if (!chatRules.some(rule => normalizeLanguageKey(rule.language) === normalizeLanguageKey(language))) {
        chatRules.push({ mode: 'chat', language: normalizeLanguageKey(language), message: fallback });
      }
    });
  }
  return chatRules.length ? [...rules.filter(rule => rule.mode !== 'chat'), ...chatRules] : rules;
};

export const setAgentMessageRules = (agent, section, key, rules) => {
  const result = section
    ? { ...agent, [section]: { ...agent[section], [`${key}_rules`]: rules } }
    : { ...agent, [`${key}_rules`]: rules };
  const legacyKey = legacyChatMessageKeys[key];
  const chat = { ...agent.chat };
  [legacyKey, `${legacyKey}_rules`, `${legacyKey}_language_wise`].forEach(field => delete chat[field]);
  return { ...result, chat };
};
